import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { IconButton } from '@material-ui/core'
import Icon from '../../../atoms/Icon'
import { useToolContext } from '../../ToolProvider'
import { useDenaliFilter } from '../filters/DenaliFilterContextProvider'
import { useDenaliData } from '../../../../api/coreData'
import { useHideIfNoDataWhen } from '../../HideIfNoData'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import OpalPresentationTable from './presentation'

const mapAccessorKey = column => column.accessorKey ? column.accessorKey : column.accessor
const mapSize = column => column.size ? column.size : column.width
const mapMaxSize = column => column.maxSize ? column.maxSize : column.maxWidth
const mapMinSize = column => column.minSize ? column.minSize : column.minWidth
const mapHeader = column => column.header ? column.header : column.Header

export const useColumns = (columns, totals) => {
  const { formatter } = useFormattingContext()
  const toolContext = useToolContext()

  return useMemo(() => {
    const defaultSort = []
    const includeFooter = totals?.length > 0
    const templatedColumns = columns.map((column) => {
      const result = {
        ...column,
        header: mapHeader(column),
        accessorKey: mapAccessorKey(column),
        size: mapSize(column),
        maxSize: mapMaxSize(column),
        minSize: mapMinSize(column)
      }

      if (!result?.cell) {
        result.cell = (props) => {
          const emptyState = column.emptyState ? column.emptyState : '--'
          let cellContent = column.hideContent ? emptyState : formatter(props.getValue(), column.format)
          const tool = toolContext[column.useTool]
          const hasClickableData = tool && (column.useTool !== 'thingDialog' || (column.useTool && !!props.getValue()))
          if (column.clickable && hasClickableData) {
            const onClick = () => {
              const staticKeys = column.staticKeys ?? []

              const config = {
                data: props.row.original,
                value: props.getValue()
              }

              if (column.dialogConfig) {
                Object.keys(column.dialogConfig).forEach(key => {
                  const fieldName = column.dialogConfig[key]

                  if (staticKeys.includes(key)) {
                    config[key] = column.dialogConfig[key]
                    return
                  }

                  if (typeof fieldName === 'string' && props.row.original[fieldName] !== undefined) {
                    config[key] = props.row.original[fieldName]
                  }
                })
              }

              tool.open(config)
            }
            cellContent = (
              <div className='clickable' onClick={onClick}>
                {column.icon ? (
                  <IconButton size='small'>
                    <Icon name={column.icon} />
                  </IconButton>
                ) : cellContent}
              </div>
            )
          }

          return cellContent
        }
      }
      if (includeFooter && !result?.footer) {
        const total = totals.at(0)
        result.footer = () => formatter(total[mapAccessorKey(column)], column.format)
      }

      return result
    })

    return {
      columns: templatedColumns,
      defaultSort,
      includeFooter
    }
  }, [formatter, columns, totals, toolContext])
}

function useDenaliDataQuery (reportId, baseQuery, totalQuery) {
  const { filter, membershipFilter } = useDenaliFilter()

  const query = useMemo(() => {
    const q = [{
      key: 'data',
      req: {
        ...baseQuery,
        membershipFilter,
        filters: {
          ...(filter || {}),
          ...(baseQuery?.filters || {})
        },
        reportId
      }
    }]

    if (totalQuery) {
      q.push({
        key: 'total',
        req: {
          ...totalQuery,
          membershipFilter,
          filters: {
            ...(filter || {}),
            ...(baseQuery?.filters || {})
          },
          reportId
        }
      })
    }

    return q
  }, [reportId, baseQuery, totalQuery, filter, membershipFilter])

  const { data, isLoading } = useDenaliData(query)

  return {
    data,
    isLoading
  }
}

function DenaliPresentationTable ({
  reportId,
  columnConfig,
  baseQuery,
  totalQuery,
  headerMode,
  className,
  style,
  componentClasses,
  componentStyles,
  emptyStateText = 'No Data Available'
}) {
  const { data, isLoading } = useDenaliDataQuery(reportId, baseQuery, totalQuery)

  /** Derive the column configuration, sorting, and footer */
  const { columns, defaultSort, includeFooter } = useColumns(columnConfig, data?.total || [])

  /** Compatibility with <HideIfNoData /> */
  useHideIfNoDataWhen(() => !isLoading && !data?.data?.length, [data, isLoading])

  return (
    <OpalPresentationTable
      className={className}
      componentClasses={componentClasses}
      componentStyles={componentStyles}
      style={style}
      data={data?.data || []}
      columns={columns}
      defaultSort={defaultSort}
      includeHeader={headerMode !== 'hidden'}
      includeFooter={includeFooter}
      isLoading={isLoading}
      emptyStateText={emptyStateText}
    />
  )
}

DenaliPresentationTable.propTypes = {
  /** The code name of the denali analysis report */
  reportId: PropTypes.string,

  /** The table column configuration / mapping */
  columnConfig: PropTypes.array,

  /** The base denali analysis query */
  baseQuery: PropTypes.object,

  /** The denali analysis query to get table totals */
  totalQuery: PropTypes.object,

  /** Header mode */
  headerMode: PropTypes.oneOf(['hidden', 'default']),

  className: PropTypes.string,
  style: PropTypes.object,

  /** Specifies classes to be applied to subcomponents of the table */
  componentClasses: PropTypes.shape({
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    header: PropTypes.object,
    cell: PropTypes.object,
    footer: PropTypes.object
  }),
  /** Empty state text, set to an empty string to hide */
  emptyStateText: PropTypes.string
}

DenaliPresentationTable.defaultProps = {
  headerMode: 'default'
}

export default DenaliPresentationTable
